import { useEffect, useState, type JSX } from 'react';
import { mensagemDoErro } from '../lib/api.js';
import { origemDoPreco } from '../lib/formato.js';
import { lerDecimalBr } from '../lib/numero.js';
import {
  dicaCaixaFechada,
  mesmoCentavo,
  moedaBr,
  percentualBr,
  quantidadeBr,
  useGerador,
  type EdicaoItem,
  type ItemOrcamento,
} from '../estado/gerador.js';
import type { ModoGerador } from '../estado/modo.js';
import { SeletorComissao } from './SeletorComissao.js';
import './LinhaItem.css';

/** O tamanho da coluna `quote_item.ambiente` (S9) — o servidor recusa acima disso. */
const AMBIENTE_MAX = 60;

/**
 * Uma linha de item do orçamento — Story 8.3, com as células de negociação do
 * item 03 do feedback pós-staging.
 *
 * ## Edição por confirmação, não por tecla
 *
 * Cada campo guarda um rascunho local e só chama o servidor no `blur` (ou
 * Enter), e apenas se o valor mudou. Salvar a cada tecla dispararia um
 * recálculo do orçamento inteiro por dígito digitado — e o vendedor veria o
 * total dançando enquanto digita "13,7".
 *
 * ## Só o PREÇO é editável por item — Story C
 *
 * RT e frete já foram células daqui, em R$/m² por linha (itens 03/04). Estava
 * errado: a planilha tem UM RT (%) e UM frete (R$/kg) para o orçamento inteiro,
 * e o resultado de expor a conta por linha, sem unidade escrita, foi o
 * ORC-2026-0007 — `5` digitado querendo "5%" virou R$ 5,00/m².
 *
 * Os dois viraram campos do CABEÇALHO (`ParametrosOrcamento`). O preço-base
 * ficou, porque é decisão da LINHA: o caso do palete, do item 04.
 *
 * A proteção continua a mesma: a célula **nunca nasce vazia**. Chega com o
 * valor do servidor e só vira override se alguém digitar por cima — um campo em
 * branco que zera em silêncio, como o `rt_base_item` que a Story 8.3 removeu,
 * não é possível aqui.
 *
 * Duas regras guardam a fronteira, nesta ordem:
 *
 * 1. **Texto intocado ⇒ nada é enviado.** Comparação de string, antes de
 *    qualquer conversão.
 * 2. **Valor igual AO CENTAVO ao calculado ⇒ o override é REMOVIDO**, não
 *    regravado. É assim que o vendedor desfaz um ajuste sem procurar botão.
 *
 * O que de fato impede o bug do R1 é a **regra 2**: a célula mostra duas casas
 * e o servidor guarda seis, então sair do campo sem editar tentaria gravar
 * `77,10` por cima de `77,098041` — um override que ninguém pediu, mudando o
 * total. A comparação ao centavo reconhece que são o mesmo número e não grava
 * nada.
 *
 * A regra 1 é defesa em profundidade, e está aqui declarada como tal: ela pega
 * o caso trivial sem sequer converter, e continua valendo se um dia a
 * formatação da célula mudar e a comparação numérica deixar de casar.
 */
export function LinhaItem({
  item,
  editavel,
  modo = 'comum',
}: {
  item: ItemOrcamento;
  editavel: boolean;
  /** `del_credere` acrescenta o seletor de coluna. Nada mais muda. */
  modo?: ModoGerador;
}): JSX.Element {
  const { editarItem, removerItem, salvando } = useGerador();
  /**
   * O rótulo do padrão do orçamento — o que vale quando o item não escolhe.
   *
   * Sai do servidor (`delNivelPadraoRotulo`), não montado aqui: é o mesmo
   * pareamento que a trilha e o seletor usam, e uma segunda definição
   * divergiria — o defeito que o `logs-frases` custou a consertar.
   */
  const rotuloPadrao = useGerador((s) => s.orcamento?.delNivelPadraoRotulo) ?? undefined;
  /*
   * A tabela do ORÇAMENTO, para dizer de onde saiu o preço base — S12. Lida do
   * store como o rótulo acima: é dado do orçamento, e passar por prop obrigaria
   * o editor e o cartão do celular a repassarem um valor que a linha já alcança.
   */
  const tabela = useGerador((s) => s.orcamento?.tabela) ?? '';

  const [quantidade, setQuantidade] = useState(item.quantidade);
  const [desconto, setDesconto] = useState(paraPercentual(item.desconto));
  const [editandoPreco, setEditandoPreco] = useState(false);
  /** Enviando o override — trava os botoes do painel sem fecha-lo. */
  const [salvandoPreco, setSalvandoPreco] = useState(false);
  /** Erro do PATCH, mostrado DENTRO do painel, ao lado do campo que o causou. */
  const [erroPainel, setErroPainel] = useState<string | undefined>(undefined);
  /** Motivo de uma edição recusada, mostrado ao lado do campo que a gerou. */
  const [aviso, setAviso] = useState<{ campo: string; texto: string } | undefined>(undefined);
  // Parte do motivo já registrado: reescrevê-lo do zero a cada ajuste era o
  // caminho mais curto para a justificativa se perder.
  const [motivo, setMotivo] = useState(item.precoManualMotivo ?? '');
  /** Cômodo da obra (S9) — rascunho local, confirmado no blur como as outras células. */
  const [ambiente, setAmbiente] = useState(item.ambiente ?? '');

  /*
   * As três células de dinheiro nascem com o valor EFETIVO do servidor,
   * formatado como a tela mostra. Guardar o texto exibido (e não o número) é o
   * que permite a regra 1: "se o texto não mudou, não houve edição".
   */
  // `rt` e `frete` saíram na Story C: viraram campos do CABEÇALHO. Só o
  // preço continua editável por item (item 04, o caso do palete).
  const [preco, setPreco] = useState(moedaBr(item.baseAvista));

  // O servidor é a fonte: quando ele responde (inclusive normalizando o número
  // digitado), o campo passa a refletir o que foi de fato gravado.
  useEffect(() => {
    setQuantidade(item.quantidade);
    setDesconto(paraPercentual(item.desconto));
    setPreco(moedaBr(item.baseAvista));
    setMotivo(item.precoManualMotivo ?? '');
    setAmbiente(item.ambiente ?? '');
  }, [
    item.quantidade,
    item.desconto,
    item.baseAvista,
    item.precoManual,
    item.precoManualMotivo,
    item.ambiente,
  ]);

  const unidade = item.unidade === 'metro_linear' ? 'm' : 'm²';
  const dica = dicaCaixaFechada(item);
  const delCredere = modo === 'del_credere';
  /**
   * As colunas do quadrante, com preço — ou `null` onde não há.
   *
   * Desde a S4 o servidor manda as SEIS, inclusive as sem preço, porque o
   * seletor novo mostra a indisponível marcada em vez de escondê-la.
   */
  const opcoes = item.precosDelCredere ?? [];
  /*
   * O `<select>` do del credere continua vendo só as vendáveis.
   *
   * A S4 não mexe neste quadrante (a unificação é a S4b), e uma `<option>` sem
   * preço aqui seria escolhível — o `select` salva no `onChange` e não tem para
   * onde recusar. Filtrar mantém o comportamento byte a byte como era.
   */
  const opcoesComPreco = opcoes.filter((o) => o.preco !== null);

  /**
   * Quantas colunas a linha tem — para o `colSpan` do painel de override.
   *
   * Estava fixo em `12 : 11` e **sobrava dois**: é o resquício das células de
   * RT e frete, removidas na Story C. O navegador trunca colSpan excedente em
   * silêncio, então nunca apareceu no desktop.
   *
   * Derivar das mesmas condições que renderizam as células é o que impede o
   * número de envelhecer de novo. São **oito fixas** — referência, formato,
   * quantidade, desconto, preço-base, unitário, faturado e total — mais a
   * coluna de escolha e a de ações.
   *
   * S4: a coluna de escolha deixou de ser condicional. Ela existe nos dois
   * quadrantes — `Coluna (del credere)` num, `Comissão` no outro.
   */
  const colunas = 9 + (editavel ? 1 : 0);

  /**
   * Devolve o campo ao valor do servidor **e diz por quê**.
   *
   * Reverter calado é quase tão ruim quanto deixar o valor errado: o vendedor
   * digita, o número volta sozinho ao anterior, e ele tenta de novo achando que
   * "não pegou". O aviso some assim que ele volta a digitar.
   */
  function reverter(campo: CampoLinha, porque: string): void {
    if (campo === 'quantidade') setQuantidade(item.quantidade);
    else if (campo === 'desconto') setDesconto(paraPercentual(item.desconto));
    else if (campo === 'preco') setPreco(moedaBr(item.baseAvista));
    else if (campo === 'ambiente') setAmbiente(item.ambiente ?? '');
    /*
     * O seletor não tem rascunho local: ele lê `item.delNivel` direto. Numa
     * recusa, o valor exibido já é o do servidor — só falta dizer por quê.
     */
    setAviso({ campo, texto: porque });
  }

  function confirmar(campo: 'quantidade' | 'desconto', valor: string): void {
    const limpo = normalizar(valor);
    setAviso(undefined);

    /*
     * Entrada inválida ou apagada **reverte**, nunca fica na tela.
     *
     * Antes isto era um `return` seco: nada era enviado, nada era avisado e o
     * campo continuava exibindo o texto do usuário. A linha passava a mostrar
     * uma quantidade/desconto que NÃO eram os usados no unitário e no total ao
     * lado — e o vendedor podia emitir (imutável!) confiando no que lia.
     * Apagar o desconto para "zerar a promoção" era o caminho mais fácil de
     * cair nisso.
     */
    if (limpo === undefined) {
      reverter(campo, valor.trim() === '' ? 'Informe um número.' : 'Número inválido.');
      return;
    }

    if (campo === 'desconto') {
      const n = Number(limpo);
      // 100% ou mais não existe no motor (RF-19 passo 3): o servidor recusa e
      // o campo voltaria a divergir do valor real. Barrado antes de sair.
      if (n >= 100) {
        reverter(campo, 'O desconto precisa ser menor que 100%.');
        return;
      }
      // A UI fala em "%" e o backend guarda fração — 5 vira 0.05.
      const fracao = (n / 100).toFixed(6);
      if (fracao === Number(item.desconto).toFixed(6)) return;
      void editarItem(item.itemId, { desconto: fracao });
      return;
    }
    if (Number(limpo) === Number(item[campo])) return;
    void editarItem(item.itemId, { [campo]: limpo });
  }

  /**
   * Confirma a célula de PREÇO-BASE — a única de dinheiro que sobrou por item.
   *
   * RT e frete saíram na Story C: viraram campos do cabeçalho, em % e R$/kg.
   * O preço continua aqui porque é decisão da LINHA (o caso do palete, item
   * 04), não do orçamento.
   *
   * A regra 1 do bloco de doc do componente continua valendo: texto idêntico ao
   * renderizado não vira requisição. A regra 2 (voltar ao calculado remove o
   * override) nunca se aplicou ao preço — para isso existe o botão do painel.
   */
  async function confirmarValor(campo: 'preco', valor: string): Promise<void> {
    setAviso(undefined);

    // REGRA 1 — texto idêntico ao renderizado: nem converte. Defesa em
    // profundidade; quem realmente segura o caso é a comparação ao centavo.
    if (valor === moedaBr(item.baseAvista)) return;

    const limpo = normalizar(valor);
    if (limpo === undefined) {
      reverter(campo, valor.trim() === '' ? 'Informe um número.' : 'Número inválido.');
      return;
    }

    /*
     * O preço não tem a regra 2: para voltar ao preço da tabela existe o
     * botão do painel. O valor de tabela não é devolvido enquanto há
     * override — e inventar uma comparação sem ele seria adivinhar.
     */
    if (mesmoCentavo(limpo, item.baseAvista)) return;
    await enviar(campo, { precoManual: limpo });
  }

  /**
   * Envia a edição de uma célula de dinheiro e **espera a resposta**.
   *
   * Se a API recusar, a célula volta ao valor do servidor com o motivo ao lado.
   * É a lição da dívida D-02 aplicada onde ela nasceu: um campo que continua
   * exibindo o número digitado depois de uma falha mostra como salvo um valor
   * que o servidor não tem — e o vendedor emite (imutável!) confiando no que lê.
   * A faixa de erro no topo da tela não resolve: fica a meia tela de distância
   * do campo que a causou.
   */
  async function enviar(campo: CampoLinha, edicao: EdicaoItem): Promise<void> {
    const ok = await editarItem(item.itemId, edicao);
    if (!ok) reverter(campo, mensagemDoErro(useGerador.getState().erro));
  }

  /**
   * Confirma o AMBIENTE — S9. Texto livre, então não há conversão: só aparar.
   *
   * A regra 1 vale aqui também: igual ao gravado (já aparado) não vira
   * requisição — passar o foco pela linha não pode recalcular o orçamento. Vazio
   * vai como `null`, que é como o servidor guarda "sem ambiente".
   */
  async function confirmarAmbiente(valor: string): Promise<void> {
    setAviso(undefined);
    const limpo = valor.trim();
    if (limpo === (item.ambiente ?? '')) {
      // Reescreve aparado: "  COZINHA " na tela seria um texto que não está gravado.
      if (valor !== limpo) setAmbiente(limpo);
      return;
    }
    await enviar('ambiente', { ambiente: limpo === '' ? null : limpo });
  }

  /**
   * O painel do preço só fecha **depois** que a API confirma (D-02).
   *
   * Antes ele disparava o `PATCH` com `void` e fechava na linha seguinte. Se a
   * chamada falhasse — rede caiu, 409 porque o orçamento foi emitido em outra
   * aba, 409 de produto sem preço na tabela — o painel já tinha fechado e a
   * linha mostrava o **preço antigo** como se fosse o novo. O erro aparecia na
   * faixa geral da tela, longe do campo que o causou. Em campo que mexe em
   * dinheiro, "salvou ou não?" é a pergunta que não pode ficar no ar.
   */
  async function aplicarMotivo(): Promise<void> {
    setErroPainel(undefined);
    setSalvandoPreco(true);
    try {
      const ok = await editarItem(item.itemId, {
        precoManualMotivo: motivo.trim() === '' ? null : motivo.trim(),
      });
      if (ok) setEditandoPreco(false);
      else setErroPainel(mensagemDoErro(useGerador.getState().erro));
    } finally {
      setSalvandoPreco(false);
    }
  }

  async function limparPrecoManual(): Promise<void> {
    setErroPainel(undefined);
    setSalvandoPreco(true);
    try {
      const ok = await editarItem(item.itemId, { precoManual: null, precoManualMotivo: null });
      if (ok) {
        setEditandoPreco(false);
        setMotivo('');
      } else {
        setErroPainel(mensagemDoErro(useGerador.getState().erro));
      }
    } finally {
      setSalvandoPreco(false);
    }
  }

  /** Célula de dinheiro editável — o mesmo desenho para RT, frete e preço. */
  function celula(
    campo: 'preco',
    rotulo: string,
    valor: string,
    setValor: (v: string) => void,
    ajustado: boolean,
    calculado: string | undefined,
  ): JSX.Element {
    return (
      <>
        <div className="linha-item__campo">
          <input
            className="entrada-num"
            inputMode="decimal"
            value={valor}
            disabled={salvando}
            aria-label={`${rotulo} do item ${item.referencia}`}
            onChange={(e) => {
              setValor(e.target.value);
              setAviso(undefined);
            }}
            onBlur={() => {
              void confirmarValor(campo, valor);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
          {aviso?.campo === campo && (
            <span className="linha-item__aviso" role="alert">
              {aviso.texto}
            </span>
          )}
        </div>
        {ajustado && (
          <span
            className="etiqueta etiqueta--aviso linha-item__marca"
            title={
              calculado === undefined
                ? 'Valor ajustado à mão neste orçamento'
                : `Ajustado à mão. O cálculo daria ${moedaBr(calculado)}.`
            }
          >
            ajustado
          </span>
        )}
      </>
    );
  }

  const algumAjuste = item.precoManual || item.rtManual === true || item.freteManual === true;

  return (
    <>
      <tr className={algumAjuste ? 'linha-item linha-item--manual' : 'linha-item'}>
        <td className="linha-item__ref" data-rotulo="Referência">
          {item.referencia}
          {/*
            Ambiente (S9) abaixo da referência, e não numa coluna nova: é
            descrição do item, como no PDF, e uma coluna a mais mexeria no
            cabeçalho e no `colSpan` do painel. Emitido sem ambiente não ganha
            nada — a linha fica como era.
          */}
          {editavel ? (
            <div className="linha-item__ambiente">
              <input
                className="entrada-ambiente"
                value={ambiente}
                maxLength={AMBIENTE_MAX}
                placeholder="Ambiente"
                disabled={salvando}
                aria-label={`Ambiente do item ${item.referencia}`}
                onChange={(e) => {
                  setAmbiente(e.target.value);
                  setAviso(undefined);
                }}
                onBlur={() => {
                  void confirmarAmbiente(ambiente);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              {aviso?.campo === 'ambiente' && (
                <span className="linha-item__aviso" role="alert">
                  {aviso.texto}
                </span>
              )}
            </div>
          ) : (
            item.ambiente !== undefined &&
            item.ambiente !== null &&
            item.ambiente !== '' && (
              <span className="linha-item__ambiente linha-item__ambiente--leitura">
                {item.ambiente}
              </span>
            )
          )}
        </td>
        <td data-rotulo="Formato">{item.formato ?? '—'}</td>

        <td className="num" data-rotulo="Quantidade">
          {editavel ? (
            <div className="linha-item__campo">
              <input
                className="entrada-num"
                inputMode="decimal"
                value={quantidade}
                disabled={salvando}
                aria-label={`Quantidade do item ${item.referencia}`}
                onChange={(e) => {
                  setQuantidade(e.target.value);
                  setAviso(undefined);
                }}
                onBlur={() => {
                  confirmar('quantidade', quantidade);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="linha-item__unidade">{unidade}</span>
              {aviso?.campo === 'quantidade' && (
                <span className="linha-item__aviso" role="alert">
                  {aviso.texto}
                </span>
              )}
            </div>
          ) : (
            `${quantidadeBr(item.quantidade)} ${unidade}`
          )}
          {/*
            O faturado por caixa fechada explicado onde a dúvida nasce: ao lado
            da quantidade. Deixa claro que cobrar mais que o pedido é regra da
            fábrica, não erro de conta.
          */}
          {dica !== undefined && <span className="linha-item__caixas">{dica}</span>}
        </td>

        <td className="num" data-rotulo="Desconto">
          {editavel ? (
            <div className="linha-item__campo">
              <input
                className="entrada-num entrada-num--curta"
                inputMode="decimal"
                value={desconto}
                disabled={salvando}
                aria-label={`Desconto do item ${item.referencia} em porcento`}
                onChange={(e) => {
                  setDesconto(e.target.value);
                  setAviso(undefined);
                }}
                onBlur={() => {
                  confirmar('desconto', desconto);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="linha-item__unidade">%</span>
              {aviso?.campo === 'desconto' && (
                <span className="linha-item__aviso" role="alert">
                  {aviso.texto}
                </span>
              )}
            </div>
          ) : (
            percentualBr(item.desconto)
          )}
        </td>

        {/*
          Seletor de COLUNA — del credere (Épico 9, Story 9.4).

          Lá a coluna é decisão comercial do vendedor, e o seletor mostra o
          preço de cada opção porque escolher às cegas obrigaria a trocar uma
          por uma só para comparar.

          Salva direto no `onChange`, e não no `blur` como as células de
          dinheiro: um `<select>` só muda quando alguém escolhe outra coisa —
          não há rascunho a confirmar, nem risco de gravar o valor exibido por
          engano.

          ⚠️ Continua `<select>` de propósito. A S4 estreia o campo digitável
          nas tabelas SEM del; unificar os dois é a S4b, depois que o padrão
          novo tiver rodado em produção. Este é o quadrante com emitidos.
        */}
        {delCredere && (
          <td className="linha-item__nivel" data-rotulo="Coluna">
            {editavel ? (
              <>
                <select
                  className="entrada-nivel"
                  value={item.delNivel ?? ''}
                  disabled={salvando || opcoesComPreco.length === 0}
                  aria-label={`Coluna del credere do item ${item.referencia}`}
                  onChange={(e) => {
                    void enviar('nivel', { delNivel: e.target.value });
                  }}
                >
                  {opcoesComPreco.map((o) => (
                    <option key={o.nivel} value={o.nivel}>
                      {o.rotulo} — {moedaBr(o.preco)}
                    </option>
                  ))}
                </select>
                {aviso?.campo === 'nivel' && (
                  <span className="linha-item__aviso" role="alert">
                    {aviso.texto}
                  </span>
                )}
              </>
            ) : (
              (opcoes.find((o) => o.nivel === item.delNivel)?.rotulo ?? item.delNivel ?? '—')
            )}
          </td>
        )}

        {/*
          Seletor de COMISSÃO — as tabelas sem del credere (S4).

          A S1 trouxe as seis comissões para CPF e RT10, a S2 deixou o item
          escolher entre elas, e até agora o vendedor não alcançava: o
          mecanismo existia sem tela.

          Campo digitável, não `<select>` — foi pedido do cliente, e a razão
          dele tem fundamento: uma lista fechada esconde que aquilo é uma
          ESCALA. A lista continua aparecendo porque o preço de cada comissão é
          o que decide a venda.

          Vazio = sem escolha, e aí vale o padrão do orçamento ou a
          `pricing_rule`. É o caminho que 100% dos itens percorriam antes da S4,
          e é o que mantém o golden verde.
        */}
        {!delCredere && (
          <td className="linha-item__nivel" data-rotulo="Comissão">
            {editavel ? (
              <SeletorComissao
                opcoes={opcoes}
                valor={item.delNivel}
                desabilitado={salvando}
                descricao={item.referencia}
                textoPadrao={rotuloPadrao ?? 'padrão'}
                /*
                  Devolve a mensagem em vez de usar `enviar`: o seletor tem
                  rascunho local e é ele quem precisa desfazer a exibição
                  quando o servidor recusa. O `enviar` serve os campos cujo
                  valor exibido já é o do servidor.
                */
                aoEscolher={async (nivel) => {
                  const ok = await editarItem(item.itemId, { delNivel: nivel });
                  return ok ? null : mensagemDoErro(useGerador.getState().erro);
                }}
              />
            ) : (
              (opcoes.find((o) => o.nivel === item.delNivel)?.rotulo ?? rotuloPadrao ?? '—')
            )}
          </td>
        )}

        {/*
          As células de RT e FRETE saíram daqui — Story C.

          Eram editáveis em R$/m² por linha (itens 03/04). O modelo estava
          errado: a planilha tem UM RT (%) e UM frete (R$/kg) para o orçamento
          inteiro, e expor o resultado da conta por linha, sem unidade escrita,
          produziu o ORC-2026-0007 — `5` digitado querendo "5%" virou R$ 5,00/m².

          Desde a Story B a API RECUSA `rtManual`/`freteManual` no PATCH de
          item, então estas células estavam quebradas. Os dois campos agora
          vivem no cabeçalho (`ParametrosOrcamento`), com `%` e `R$/kg` ao lado.

          O RT e o frete de cada item continuam existindo — como RESULTADO, no
          unitário e nos totais do rodapé. O que sumiu foi a entrada.
        */}

        <td className="num" data-rotulo="Preço base">
          {editavel ? (
            <>
              {celula('preco', 'Preço base', preco, setPreco, item.precoManual, undefined)}
              <button
                type="button"
                className="linha-item__link"
                onClick={() => {
                  setEditandoPreco((v) => !v);
                }}
              >
                {item.precoManual ? 'motivo / desfazer' : 'motivo'}
              </button>
            </>
          ) : (
            <>
              <span className={item.precoManual ? 'preco-manual' : undefined}>
                {moedaBr(item.baseAvista)}
              </span>
              {item.precoManual && (
                <span
                  className="etiqueta etiqueta--aviso linha-item__marca"
                  title="Preço sobrescrito"
                >
                  ajustado
                </span>
              )}
            </>
          )}
          {/* De onde saiu o número — S12, CA-5. Sempre à vista, nos dois estados:
              foi não ver isto que fez o ORC-2026-0053 sair em Engenharia sem RT. */}
          <span className="linha-item__origem">{origemDoPreco(tabela, item)}</span>
        </td>

        <td className="num" data-rotulo="Unitário">
          {moedaBr(item.unit)}
        </td>
        <td className="num" data-rotulo="Faturado">
          {quantidadeBr(item.qtdFaturada)}
        </td>
        <td className="num linha-item__total" data-rotulo="Total" data-destaque="sim">
          {moedaBr(item.totalItem)}
        </td>

        {editavel && (
          <td data-rotulo="">
            <button
              type="button"
              className="botao botao--discreto linha-item__remover"
              disabled={salvando}
              aria-label={`Remover o item ${item.referencia}`}
              onClick={() => {
                void removerItem(item.itemId);
              }}
            >
              Remover
            </button>
          </td>
        )}
      </tr>

      {editavel && editandoPreco && (
        <tr className="linha-item__painel">
          <td colSpan={colunas}>
            <div className="override">
              <p className="override__aviso">
                O <strong>preço-base</strong> é editado direto na célula da linha. Aqui fica a{' '}
                <strong>justificativa</strong>, que vai para a auditoria — e o botão para devolver o
                item ao preço da tabela. IPI, desconto, frete e RT continuam sendo aplicados por
                cima. Não use para dar desconto: para isso existe a coluna de desconto.
              </p>
              <div className="override__campos">
                <label className="campo override__motivo">
                  <span className="campo__rotulo">Motivo (vai para a auditoria)</span>
                  <input
                    className="campo__entrada"
                    value={motivo}
                    placeholder="ex.: valor digitado à mão na planilha de origem"
                    onChange={(e) => {
                      setMotivo(e.target.value);
                    }}
                  />
                </label>
              </div>

              {/* O erro fica DENTRO do painel, ao lado do campo que o causou —
                  não na faixa geral, a meia tela de distância. */}
              {erroPainel !== undefined && (
                <p className="override__erro" role="alert">
                  {erroPainel}
                </p>
              )}

              <div className="override__acoes">
                {item.precoManual && (
                  <button
                    type="button"
                    className="botao botao--neutro"
                    disabled={salvandoPreco}
                    onClick={() => {
                      void limparPrecoManual();
                    }}
                  >
                    Voltar ao preço da tabela
                  </button>
                )}
                <button
                  type="button"
                  className="botao botao--neutro"
                  disabled={salvandoPreco}
                  onClick={() => {
                    setErroPainel(undefined);
                    setEditandoPreco(false);
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="botao botao--primario"
                  disabled={salvandoPreco}
                  onClick={() => {
                    void aplicarMotivo();
                  }}
                >
                  {salvandoPreco ? 'Salvando…' : 'Salvar motivo'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Campos da linha que podem exibir um aviso de recusa. */
/** `rt` e `frete` saíram na Story C — viraram campos do cabeçalho. */
type CampoLinha = 'quantidade' | 'desconto' | 'preco' | 'nivel' | 'ambiente';

/**
 * Aceita vírgula decimal — é o teclado de quem digita em pt-BR.
 *
 * ## Esta era a implementação CERTA das duas
 *
 * Havia duas cópias da mesma regra. Esta só tratava o ponto como milhar
 * **quando havia uma vírgula presente**, e por isso `1.10` aqui sempre valeu
 * 1,10. A do `gerador`, usada pelo campo de frete, removia o ponto **sempre** —
 * e lá `1.10` virava R$ 110,00/kg.
 *
 * Agora as duas são a mesma (`lib/numero.ts`). O que esta ganha de troco é a
 * recusa de `1.1.0` e `1.10.10`, que a versão local aceitava silenciosamente
 * como 1,1 — separador repetido sem grupos de três não tem leitura única.
 */
function normalizar(valor: string): string | undefined {
  const lido = lerDecimalBr(valor);
  /*
   * O contrato desta função continua `string | undefined`: quem chama já tem o
   * seu próprio `reverter()`, com a mensagem escrita no contexto da linha.
   */
  return lido.ok ? String(lido.valor) : undefined;
}

/** Fração do backend (`0.05`) → percentual da UI (`5`). */
function paraPercentual(fracao: string): string {
  const n = Number(fracao);
  if (!Number.isFinite(n)) return '0';
  return String(Number((n * 100).toFixed(4)));
}
